import Badge from "@/components/Badge/Badge"
import type { FamilyNumber } from "@/theme/colors"
import styles from "@/styles/components/AvatarStack.module.css"

/*
 * A row of overlapping people.
 *
 * The prototype uses this everywhere multiplayer has to be visible without
 * taking up a column: who voted for a pain, who nominated a card, who is in the
 * room, who has finished. Overlapping rather than spaced because the count
 * matters more than the individuals — the names are in the tooltips.
 *
 * `dimmedIds` is the "has not finished yet" state. It is a different fact from
 * being absent from the list: the seat is taken, the work is not done.
 */

export interface StackPerson {
  id: string
  name: string
  /** Overrides the name-hash tint — see `familyForName` in `Badge`. Pass the
   *  seat-ring family (by position) so the same person's colour never
   *  disagrees between the table and a stack drawn elsewhere. */
  family?: FamilyNumber
}

interface AvatarStackProps {
  people: readonly StackPerson[]
  /** Beyond this, the rest collapse into a "+N". */
  max?: number
  size?: 28 | 36
  dimmedIds?: readonly string[]
  /** Appended to each tooltip: "Marta Reis · still writing". */
  suffixFor?: (person: StackPerson) => string | null
}

export default function AvatarStack({
  people,
  max = 6,
  size = 28,
  dimmedIds,
  suffixFor,
}: AvatarStackProps) {
  if (people.length === 0) return null

  const shown = people.slice(0, max)
  const hidden = people.length - shown.length

  return (
    <span className={styles.stack}>
      {shown.map((person) => {
        const suffix = suffixFor?.(person)
        const label = suffix ? `${person.name} · ${suffix}` : person.name
        return (
          /*
           * `role="img"` with the name on it, not just `title`. The initials
           * `Badge` draws are a picture of the person and are `aria-hidden` for
           * that reason, so without this the whole row reaches a screen reader
           * as nothing at all — and `title` is announced inconsistently and
           * never reachable by keyboard, so it cannot be the only carrier. The
           * suffix is part of the name because it is part of the fact: "still
           * writing" is what the dimming means.
           *
           * Phrasing content on purpose. This sits inside a `<button>` on the
           * customer screen, where a list would not be valid.
           */
          <span
            key={person.id}
            role="img"
            aria-label={label}
            className={
              dimmedIds?.includes(person.id) ? `${styles.slot} ${styles.dim}` : styles.slot
            }
            title={label}
          >
            <Badge name={person.name} size={size} family={person.family} />
          </span>
        )
      })}
      {hidden > 0 ? (
        <span className={styles.more} role="img" aria-label={`${hidden} more`}>
          +{hidden}
        </span>
      ) : null}
    </span>
  )
}
